import { Link } from 'react-router-dom';

import { cn } from '@/_shared/lib/utils';

interface ListCardProps {
  /**
   * Destination href for the whole card (build it with `wp()`).
   *
   * Optional, for an item with no detail route to send anyone to. Settings › Accounts is the case:
   * an account has no page of its own, and the obvious card-wide action — Edit — exists for
   * key-based accounts but not for OAuth ones or for definitions Lookout renders no form for. A
   * click target that works on some cards and silently does nothing on their neighbours is worse
   * than none, so those cards carry their actions in the kebab only.
   *
   * Without it the card also drops its hover border, which would otherwise promise a click that
   * leads nowhere.
   */
  to?: string;
  title: string;
  /**
   * A leading mark rendered immediately before the title — a brand glyph or a type icon that says what
   * KIND of thing the title names. Decorative: give it `aria-hidden`, and keep the meaning in
   * `ariaLabel` (the icon sits inside the stretched link's card, not inside its accessible name).
   */
  titleIcon?: React.ReactNode;
  /**
   * Extra classes on the title itself, merged last so they win. For the case where the title is not a
   * name but a stand-in for one — an id, a fallback — and has to be SET as such rather than typeset as
   * a name the product does not actually have.
   */
  titleClassName?: string;
  /**
   * Replaces the title TEXT (not the header row, and not the badges or the actions slot) for a card
   * whose identity is a composed relation rather than one string — the activations grid renders its
   * `ActivationStrip` here, so a card and its table row are identified by the same thing.
   *
   * `title` stays required: it is still the default accessible name, and it is the string a caller
   * sorts and searches by. A node passed here owns its own truncation and its own `title` attribute —
   * the wrapper is a plain flex child, not the `truncate`d `<p>`, which would clamp it to one
   * non-shrinking line.
   */
  titleContent?: React.ReactNode;
  /** Accessible name for the card's link. Defaults to `Open <title>`. */
  ariaLabel?: string;
  /** Status pills etc., rendered inline after the title. */
  badges?: React.ReactNode;
  /** Row actions (a ⋯ menu). Rendered above the stretched link so it stays clickable. */
  actions?: React.ReactNode;
  description?: string | null;
  /** Meta line under the description — usually a few {@link MetaChip}s. */
  meta?: React.ReactNode;
  /** Bottom-left of the footer (e.g. last run). */
  footerLeft?: React.ReactNode;
  /** Bottom-right of the footer (e.g. "Updated 2h ago"). */
  footerRight?: React.ReactNode;
  className?: string;
}

/**
 * The shared card shell for a list page's card view: title + badges, an actions slot, description, a meta
 * line, and a footer pinned to the bottom so a grid of cards lines up. Stretched-link: the whole card
 * navigates via an absolute overlay while `actions` sits above it (`relative z-10`) — a `<button>` can't
 * nest inside an `<a>`.
 */
export function ListCard({
  to,
  title,
  titleIcon,
  titleClassName,
  titleContent,
  ariaLabel,
  badges,
  actions,
  description,
  meta,
  footerLeft,
  footerRight,
  className,
}: ListCardProps): React.ReactElement {
  const hasFooter = footerLeft != null || footerRight != null;
  return (
    <div
      className={cn(
        'group relative flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-colors',
        // Only when the card actually goes somewhere — see `to`.
        to && 'hover:border-grey-200',
        className,
      )}
    >
      {to && (
        <Link
          to={to}
          aria-label={ariaLabel ?? `Open ${title}`}
          className="absolute inset-0 rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        />
      )}

      <div className="flex items-start gap-2">
        {/* No `flex-wrap`: the title truncates to make room for the badges instead of pushing them onto a
            second line. The title is NOT `flex-1` — it sizes to its content so the badges sit right beside
            it; `min-w-0` still lets it shrink and truncate. The only space-between is this group vs the
            actions menu. Badges are `shrink-0` already. */}
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {/* Outside the `<p>` and `shrink-0`: the mark identifies the row at a glance, so it is the one
              thing in this group that must never be what truncates. */}
          {titleIcon && <span className="flex shrink-0 items-center">{titleIcon}</span>}
          {titleContent ?? (
            <p title={title} className={cn('min-w-0 truncate text-sm font-semibold text-foreground', titleClassName)}>
              {title}
            </p>
          )}
          {badges}
        </div>
        {actions && <div className="relative z-10 -mt-1 -mr-1 shrink-0">{actions}</div>}
      </div>

      {description && (
        <p title={description} className="mt-1 line-clamp-2 text-xs text-muted-foreground">
          {description}
        </p>
      )}

      {meta && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">{meta}</div>
      )}

      {hasFooter && (
        <div className="mt-auto flex items-center justify-between gap-2 pt-3 text-xs">
          {/* gap-2 so a footer carrying several chips or badges reads as separate items. */}
          <span className="inline-flex min-w-0 flex-wrap items-center gap-2 text-muted-foreground">{footerLeft}</span>
          {footerRight && <span className="shrink-0 whitespace-nowrap text-muted-foreground">{footerRight}</span>}
        </div>
      )}
    </div>
  );
}
